import React, { FC } from "react"
import AudioPlayerSlider from "./AudioPlayerSlider"
import tailwindConfig from 'src/../tailwind.config'
import resolveConfig from 'tailwindcss/resolveConfig'
import { CustomColorConfig } from "../calendar/calendar-color-mapping"

interface IAudioProgressProps {
  duration: number
  progress: number
  onInput: (value: number) => void
  onChange: (value: number) => void
  className?: string
}

const colors = resolveConfig(tailwindConfig).theme.colors as CustomColorConfig

function isFiniteNumber(x: number): boolean {
  return !isNaN(x) && isFinite(x)
}

const formatDuration = (audioDuration: number): string => {
  if (isFiniteNumber(audioDuration)) {
    const hours = Math.floor(audioDuration / 3600)
    const minutes = Math.floor((audioDuration % 3600) / 60)
    const seconds = Math.round(audioDuration % 60)
    const secondsPart = seconds.toString().padStart(2, '0')
    return hours
      ? `${hours.toString()}:${minutes.toString().padStart(2, '0')}:${secondsPart}`
      : `${minutes}:${secondsPart}`
  }
  return '-:--'
}

const AudioProgress: FC<IAudioProgressProps> = ({
  duration, 
  progress,
  onInput,
  onChange,
  className,
}) => {
  const displayedDuration = formatDuration(duration)
  const displayedProgress = formatDuration(progress)
  const audioHasDuration = duration !== 0 || isFiniteNumber(duration)
  const maxSliderValue = audioHasDuration 
  ? Math.max(100, Math.ceil(duration)) 
  : 100

  const calculateAudioProgress = (sliderProgress: number) => {
    return audioHasDuration
      ? sliderProgress * duration / maxSliderValue
      : progress
  }
    
  const onProgressUpdateStart = (newSliderProgress: number) => {
    const audioProgress = calculateAudioProgress(newSliderProgress)
    onInput(audioProgress)
  }
  
  const onProgressUpdateEnd = (newSliderProgress: number) => {
    const audioProgress = calculateAudioProgress(newSliderProgress)
    onChange(audioProgress)
  }

  const getDisplayedProgress = () => {
    if (progress === 0) {
      return 0
    }
    if (audioHasDuration) {
      return Math.round(progress * maxSliderValue / duration)
    }
    return Math.round((maxSliderValue - 1) * (progress / (30 + progress)))    
  }

  return <div className={`flex justify-start items-center${className ? ` ${className}` : ''}`}>
    <div className="font-mono tracking-tight mr-2 text-deepDarkBlue h-auto">
      <span>{ displayedProgress }</span>
      <span className="mx-[1px]">/</span>
      <span>{ displayedDuration }</span>
    </div>
    <AudioPlayerSlider 
      max={maxSliderValue}
      value={getDisplayedProgress()} 
      step={1}
      passedColor={colors.deepDarkBlue}
      remainingColor={colors.grayBlue}
      className="w-full" 
      onUpdateStart={onProgressUpdateStart}
      onUpdateEnd={onProgressUpdateEnd}
      disabled={!audioHasDuration}
    />
  </div>
}

export default AudioProgress